import React from "react";
import { FileText, Database, Microscope, Radar, Download, ExternalLink } from "lucide-react";

const RADAR_IMG = "https://images.pexels.com/photos/34332794/pexels-photo-34332794.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const STORM_BG = "https://images.pexels.com/photos/12372778/pexels-photo-12372778.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PAPERS = [
  {
    title: "Sub-tornadic Inflow Variability in High-CAPE Environments",
    journal: "Journal of Atmospheric Sciences · 2024",
    co: "Vortex Ghost, OU SoM",
    tag: "PEER REVIEWED",
  },
  {
    title: "Mobile Mesonet Calibration Across Multi-Vehicle Arrays",
    journal: "Weather and Forecasting · 2023",
    co: "Vortex Ghost et al.",
    tag: "TECHNICAL NOTE",
  },
  {
    title: "Field Observations of the May 21, 2024 Greenfield Tornado",
    journal: "Bulletin of the AMS · 2024",
    co: "Vortex Ghost",
    tag: "CASE STUDY",
  },
];

const PROJECTS = [
  {
    icon: Radar,
    title: "Project PEDESTAL",
    desc: "Boundary-layer wind profiling within 800m of confirmed tornadoes using rugged Doppler lidar.",
    progress: 68,
  },
  {
    icon: Database,
    title: "Open Mesonet Archive",
    desc: "Every chase log, sounding, and probe drop from 2019 onward — searchable and free to use.",
    progress: 91,
  },
  {
    icon: Microscope,
    title: "Hail Forensics",
    desc: "Cross-sectional analysis of >5cm hailstones to study layered growth and embedded particulates.",
    progress: 42,
  },
];

export default function Research() {
  return (
    <div data-testid="research-page" className="grain">
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <img src={RADAR_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        <div className="container-vg relative pb-16">
          <div className="overline">Research Division</div>
          <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter mt-4 max-w-4xl">
            Field-grade <span className="text-yellow-400">science.</span>
          </h1>
          <p className="text-zinc-300 mt-6 max-w-2xl text-lg">
            Our research portfolio centers on three questions: how tornadoes form, how they decay, and what survives them.
          </p>
        </div>
      </section>

      {/* Active projects */}
      <section className="section-pad">
        <div className="container-vg">
          <div className="overline">Active Projects</div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold mt-3">What we're studying now.</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {PROJECTS.map(({ icon: Icon, title, desc, progress }) => (
              <div key={title} className="card-vg p-8" data-testid={`project-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <Icon className="w-8 h-8 text-yellow-400" />
                <h3 className="font-heading text-2xl font-semibold mt-5">{title}</h3>
                <p className="text-zinc-400 mt-3 text-sm leading-relaxed">{desc}</p>

                <div className="mt-6">
                  <div className="flex justify-between items-center text-xs font-mono uppercase text-zinc-500 mb-2 tracking-wider">
                    <span>Season Progress</span>
                    <span className="text-yellow-400">{progress}%</span>
                  </div>
                  <div className="w-full h-1 bg-white/5 overflow-hidden">
                    <div className="h-full bg-yellow-400" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Publications */}
      <section className="section-pad bg-[#0d0f12] border-y border-white/5">
        <div className="container-vg">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="overline">Publications</div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold mt-3">Cited &amp; reviewed.</h2>
              <p className="text-zinc-400 mt-5 leading-relaxed">
                Selected publications and case studies authored or co-authored by Vortex Ghost researchers.
              </p>
            </div>
            <div className="lg:col-span-8 space-y-4">
              {PAPERS.map((p, i) => (
                <div key={i} className="border border-white/10 hover:border-yellow-400 transition-colors p-6 group flex flex-col md:flex-row md:items-center gap-4" data-testid={`paper-${i}`}>
                  <FileText className="w-8 h-8 text-yellow-400 shrink-0" />
                  <div className="flex-1">
                    <div className="overline !text-sky-400">{p.tag}</div>
                    <h3 className="font-heading text-xl font-semibold mt-1">{p.title}</h3>
                    <div className="text-zinc-500 text-sm font-mono mt-1">{p.journal} · {p.co}</div>
                  </div>
                  <button className="text-zinc-400 group-hover:text-yellow-400 flex items-center gap-2 text-sm font-mono uppercase tracking-wider" data-testid={`paper-link-${i}`}>
                    <ExternalLink className="w-4 h-4" /> View
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Open data CTA */}
      <section className="relative overflow-hidden section-pad">
        <img src={STORM_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
        <div className="container-vg relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline">Open Data</div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mt-3">
              Every observation we've ever made — yours.
            </h2>
            <p className="text-zinc-400 mt-5 max-w-lg leading-relaxed">
              We believe atmospheric science moves faster when data is free. Our entire field archive from 2019 to
              present is publicly downloadable.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#" className="btn-primary" data-testid="research-download-data"><Download className="w-4 h-4" /> Browse archive</a>
            </div>
          </div>
          <div className="glass p-8">
            <div className="overline">Archive At A Glance</div>
            <div className="mt-6 space-y-5 font-mono text-sm">
              <div className="flex justify-between border-b border-white/10 pb-3">
                <span className="text-zinc-500">Chase logs</span>
                <span className="text-white">412</span>
              </div>
              <div className="flex justify-between border-b border-white/10 pb-3">
                <span className="text-zinc-500">Soundings released</span>
                <span className="text-white">87</span>
              </div>
              <div className="flex justify-between border-b border-white/10 pb-3">
                <span className="text-zinc-500">Mesonet samples</span>
                <span className="text-white">2.4M</span>
              </div>
              <div className="flex justify-between border-b border-white/10 pb-3">
                <span className="text-zinc-500">Confirmed tornadoes</span>
                <span className="text-yellow-400">38</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Storage size</span>
                <span className="text-white">1.7 TB</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
